import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardMedia, 
  CardContent, 
  CardActions,
  Skeleton,
  useTheme,
  useMediaQuery,
  IconButton
} from '@mui/material';
import { motion } from 'framer-motion';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link as RouterLink } from 'react-router-dom';

/**
 * Component for displaying active promotions in a banner
 */
const PromotionBanner = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Fetch active promotions
  useEffect(() => {
    const fetchPromotions = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/promotions', {
          params: {
            activeOnly: true,
            limit: 10
          }
        });
        
        if (response.data && response.data.promotions && response.data.promotions.length > 0) {
          setPromotions(response.data.promotions);
        } else {
          // No active promotions
          setPromotions([]);
        }
        setError(null);
      } catch (err) {
        console.error('Error fetching promotions:', err);
        setError('Не удалось загрузить акции');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPromotions();
  }, []);

  // Auto-rotate promotions every 7 seconds
  useEffect(() => {
    if (promotions.length <= 1) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % promotions.length);
    }, 7000);
    
    return () => clearInterval(interval);
  }, [promotions.length]);

  // Handle manual navigation
  const handlePrevPromotion = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? promotions.length - 1 : prevIndex - 1
    );
  };

  const handleNextPromotion = () => {
    setCurrentIndex((prevIndex) => 
      (prevIndex + 1) % promotions.length
    );
  };

  // If no promotions or error, don't render anything
  if ((!loading && promotions.length === 0) || error) {
    return null;
  }

  return (
    <Box 
      sx={{ 
        width: '100%',
        mt: 1,
        mb: 1,
        position: 'relative'
      }}
    >
      {loading ? (
        <Card 
          elevation={3}
          sx={{ 
            borderRadius: 2,
            overflow: 'hidden',
            backgroundColor: theme.palette.background.paper,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            height: isMobile ? 'auto' : '160px'
          }}
        >
          {!isMobile && (
            <Skeleton 
              variant="rectangular" 
              width={240} 
              height={160} 
              animation="wave"
            />
          )}
          <CardContent sx={{ flex: 1, p: 3 }}>
            <Skeleton variant="text" width="60%" height={40} />
            <Skeleton variant="text" width="90%" height={20} sx={{ mt: 1 }} />
            <Skeleton variant="text" width="80%" height={20} sx={{ mt: 1 }} />
            <Skeleton variant="rectangular" width={120} height={36} sx={{ mt: 2 }} />
          </CardContent>
        </Card>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <Card 
            elevation={4}
            sx={{ 
              borderRadius: 3,
              overflow: 'hidden',
              backgroundColor: theme.palette.background.paper,
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              height: isMobile ? 'auto' : '160px',
              position: 'relative'
            }}
          >
            {promotions[currentIndex].imageUrl && (
              <CardMedia
                component="img"
                sx={{ 
                  width: isMobile ? '100%' : 240,
                  height: isMobile ? 160 : '100%',
                  objectFit: 'cover'
                }}
                image={promotions[currentIndex].imageUrl}
                alt={promotions[currentIndex].title}
              />
            )}
            <CardContent sx={{ flex: 1, p: 2 }}>
              <Typography variant="h6" component="h2" gutterBottom fontWeight="bold" sx={{ mb: 0.5 }}>
                {promotions[currentIndex].title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {promotions[currentIndex].description}
              </Typography>
              <CardActions sx={{ p: 0, pt: 0 }}>
                {promotions[currentIndex].buttonLink && (
                  <Button 
                    variant="contained" 
                    color="primary" 
                    component={RouterLink}
                    to={promotions[currentIndex].buttonLink}
                    endIcon={<ArrowForwardIcon fontSize="small" />}
                    size="small"
                  >
                    {promotions[currentIndex].buttonText || 'Подробнее'}
                  </Button>
                )}
              </CardActions>
            </CardContent>
            
            {/* Navigation arrows (only if more than one promotion) */}
            {promotions.length > 1 && (
              <>
                <IconButton 
                  sx={{ 
                    position: 'absolute',
                    left: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
                    },
                    zIndex: 10
                  }}
                  onClick={handlePrevPromotion}
                  size="small"
                >
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <IconButton 
                  sx={{ 
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    backgroundColor: 'rgba(255,255,255,0.8)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.95)',
                      boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
                    },
                    zIndex: 10
                  }}
                  onClick={handleNextPromotion}
                  size="small"
                >
                  <ArrowForwardIcon fontSize="small" />
                </IconButton>
                
                {/* Dots indicator */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 8,
                    left: 0,
                    right: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    gap: 1.2,
                    zIndex: 10
                  }}
                >
                  {promotions.map((_, index) => (
                    <Box
                      key={index}
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        backgroundColor: index === currentIndex ? theme.palette.primary.main : 'rgba(0,0,0,0.2)',
                        boxShadow: index === currentIndex ? '0 0 4px rgba(0,0,0,0.3)' : 'none',
                        transition: 'all 0.2s ease',
                        transform: index === currentIndex ? 'scale(1.2)' : 'scale(1)',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: index === currentIndex ? theme.palette.primary.main : 'rgba(0,0,0,0.4)',
                        }
                      }}
                      onClick={() => setCurrentIndex(index)}
                    />
                  ))}
                </Box>
              </>
            )}
          </Card>
        </motion.div>
      )}
    </Box>
  );
};

export default PromotionBanner;
